/**
 * Polarium affiliate attribution (Quadcode partner cabinet). The cabinet builds every partner link
 * from a landing template with `:aff:` (partner id), `:model:` (aff_model) and `:afftrack:` (free
 * sub-id); the OAuth authorize URL takes the same three (`aff`, `aff_model`, `afftrack`) and hands
 * them to Polarium's sign-up screen, so an account created through our "Conectar" button is the
 * partner's. Accounts that already exist keep their attribution.
 *
 * The cabinet's sub-affiliate link (Estatísticas › Subafiliados, `/statistics/referral`) is the
 * partner sign-up with `ref_id` = the same partner id that fills `:aff:` (both are the cabinet's
 * `user.id`, read 2026-10-24), plus `source` and an optional `afftrack`.
 */
export interface PolariumAffiliate {
  affId: number;
  affModel: string | null;
  afftrack: string | null;
}

/** Sub-id sent when the partner's link has none: their Polarium statistics show the lead came through Invexa. */
export const POLARIUM_DEFAULT_AFFTRACK = 'invexa';

const MAX_AFF_ID = 2_147_483_647;
const MAX_AFFTRACK = 64;

/** Partner id as Polarium reads it: a positive integer (their lookup refuses anything else). */
function affIdOf(raw: string | null | undefined): number | null {
  const v = raw?.trim() ?? '';
  if (!/^\d{1,10}$/.test(v)) return null;
  const n = Number(v);
  return n > 0 && n <= MAX_AFF_ID ? n : null;
}

/** The cabinet's own models are short lowercase words (revenue, cpa, hybrid, turnover…). */
function modelOf(raw: string | null | undefined): string | null {
  const v = raw?.trim().toLowerCase() ?? '';
  return /^[a-z0-9_-]{1,32}$/.test(v) ? v : null;
}

/** Sub-id: letters, digits and _ . - (what the cabinet keeps); anything else is dropped. */
function afftrackOf(raw: string | null | undefined): string | null {
  const v = (raw ?? '').replace(/[^A-Za-z0-9_.-]/g, '').slice(0, MAX_AFFTRACK);
  return v || null;
}

/** An affiliate's own field takes the client link: a sub-affiliate link there is a copy of the wrong link. */
export const POLARIUM_SUBAFFILIATE_IN_CLIENT_FIELD =
  'Esse é um link de subafiliado (cadastro de parceiros), não serve aqui. Cole o link de afiliado para clientes (com aff=…), gerado no painel de parceiros da Polarium, ou só o número do ID.';

export type ParsedPolariumLink =
  | {
      ok: true;
      value: PolariumAffiliate;
      /** 'subaffiliate': the cabinet's sub-affiliate link (partner sign-up, id in `ref_id`). */
      kind: 'client' | 'subaffiliate';
    }
  | { ok: false; error: string };

/** A link pasted with text around it ("Meu link: https://…"): the part that carries the id. */
function linkPart(input: string): string {
  const t = input.trim();
  return /\s/.test(t) ? (/\S*[?&](?:aff|aff_id|ref_id)=\S*/.exec(t)?.[0] ?? t) : t;
}

function toUrl(text: string): URL | null {
  try {
    return new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(text) ? text : `https://${text}`);
  } catch {
    return null;
  }
}

/**
 * Reads what a partner pastes: the link copied from the Polarium partner cabinet (any landing,
 * with or without https://) or just their numeric partner id. Short links (bit.ly…) carry no id
 * and are refused with a message saying what to paste instead.
 */
export function parsePolariumAffiliateLink(input: string): ParsedPolariumLink {
  const text = linkPart(input);
  if (!text) return { ok: false, error: 'Cole o seu link de afiliado da Polarium.' };
  const bare = affIdOf(text);
  if (bare) return { ok: true, value: { affId: bare, affModel: null, afftrack: null }, kind: 'client' };
  const url = toUrl(text);
  if (!url) return { ok: false, error: 'Isso não parece um link. Copie o link completo do painel de parceiros da Polarium.' };
  const q = url.searchParams;
  const clientAff = q.get('aff') ?? q.get('aff_id');
  const rawAff = clientAff ?? q.get('ref_id');
  if (rawAff === null) {
    return {
      ok: false,
      error: 'Esse link não tem o seu ID de afiliado (aff=…). Links encurtados não servem: copie o link gerado no painel de parceiros da Polarium, ou digite só o número do seu ID.',
    };
  }
  const affId = affIdOf(rawAff);
  if (!affId) return { ok: false, error: 'O ID de afiliado do link (aff=…) não é um número válido. Gere o link de novo no painel de parceiros da Polarium.' };
  return {
    ok: true,
    value: { affId, affModel: modelOf(q.get('aff_model') ?? q.get('affModel')), afftrack: afftrackOf(q.get('afftrack') ?? q.get('aff_track')) },
    kind: clientAff === null ? 'subaffiliate' : 'client',
  };
}

/**
 * The sub-affiliate link to open from the affiliate panel ("cadastre-se como parceiro"): only an
 * https link carrying a valid `ref_id`, as the cabinet gives it; anything else is null.
 */
export function polariumSubaffiliateUrl(input: string | null | undefined): string | null {
  if (!input) return null;
  const parsed = parsePolariumAffiliateLink(input);
  if (!parsed.ok || parsed.kind !== 'subaffiliate') return null;
  const url = toUrl(linkPart(input));
  return url?.protocol === 'https:' ? url.href : null;
}

/** The sub-affiliate field takes only the cabinet's sub-affiliate link. */
export const POLARIUM_NOT_SUBAFFILIATE =
  'Esse não é o link de subafiliado. Copie no painel de parceiros da Polarium, em Estatísticas › Subafiliados (o link tem ref_id=…).';

/**
 * Reads a link for one of the two fields: 'client' (the affiliate link that goes on the "Conectar"
 * sign-up: an affiliate's own, or the platform's) refuses a sub-affiliate link; 'subaffiliate'
 * (the platform's partner sign-up link) takes only an https link with `ref_id`.
 */
export function parsePolariumLinkFor(input: string, field: 'client' | 'subaffiliate'): ParsedPolariumLink {
  const parsed = parsePolariumAffiliateLink(input);
  if (field === 'subaffiliate') {
    if (!input.trim()) return { ok: false, error: 'Cole o link de subafiliado da Polarium.' };
    return parsed.ok && polariumSubaffiliateUrl(input) ? parsed : { ok: false, error: POLARIUM_NOT_SUBAFFILIATE };
  }
  return parsed.ok && parsed.kind === 'subaffiliate' ? { ok: false, error: POLARIUM_SUBAFFILIATE_IN_CLIENT_FIELD } : parsed;
}
